<template>
    <div class="graph-pie">
        <canvas ref="canvas"></canvas>
    </div>
</template>

<script>
import debounce from 'lodash/debounce';
import { mapState } from 'pinia';
import { Chart, registerables } from 'chart.js';
import { defineComponent } from 'vue';
import { useScreenWidthStore } from '@/stores/utils/screenWidth';

Chart.register(...registerables);

export default defineComponent({
  name: 'GraphPie',
  props: {
    chartData: { type: Object, required: true },
    chartOptions: { type: Object, default: () => ({}) },
    disableTooltip: { type: Boolean, default: false }
  },
  computed: {
    ...mapState(useScreenWidthStore, ['hasMediumScreen']),
  },
  data() {
    return {
      chart: null,
      debouncedDataUpdate: null,
      isDestroyed: false,
      pauseUpdates: false,
      rafId: null,
      visibilityHandler: null,
      io: null
    };
  },
  created() {
    // Only batch rapid incoming data mutations
    this.debouncedDataUpdate = debounce(() => {
      this.applyData();
      this.scheduleUpdate();
    }, 150, { maxWait: 250 });
  },
  mounted() {
    this.createChart();
    this.setupVisibilityHandling();
    this.setupIntersectionObserver();
  },
  beforeUnmount() {
    document.removeEventListener('visibilitychange', this.visibilityHandler);
    if (this.io) {
      try {
        this.io.disconnect();
      } catch (e) {
        console.error('Error disconnecting IntersectionObserver:', e);
      }
      this.io = null;
    }
    this.destroyChart();
  },
  watch: {
    chartData: {
      handler() {
        if (!this.chart || this.isDestroyed) return;
        this.debouncedDataUpdate();
      },
      deep: true
    },
    chartOptions: {
      handler() {
        // Options changes may require full rebuild (simpler & safe)
        this.rebuildChart();
      },
      deep: true
    },
    hasMediumScreen() {
      this.rebuildChart();
    }
  },
  methods: {
    rebuildChart() {
      this.destroyChart();
      this.createChart();
    },
    baseModifiedData() {
      // Normalize dataset (remove borders)
      return {
        ...this.chartData,
        datasets: this.chartData.datasets.map(d => ({
          ...d,
            // Ensure no border (avoid extra drawing ops)
          borderWidth: 0
        }))
      };
    },
    createChart() {
      if (this.chart) return;
      const canvas = this.$refs.canvas;
      if (!canvas) return;
      try {
        const ctx = canvas.getContext('2d');
        this.isDestroyed = false;
        const data = this.baseModifiedData();
        this.chart = new Chart(ctx, {
          type: 'doughnut',
          data,
          options: {
            responsive: true,
            animation: false,
            animations: {
              colors: false,
              resize: false,
              active: false
            },
            // Only attach events if tooltips enabled
            events: (this.hasMediumScreen && !this.disableTooltip)
              ? ['mousemove', 'mouseout', 'click', 'touchstart', 'touchmove']
              : [],
            plugins: {
              legend: { display: false },
              tooltip: {
                enabled: this.hasMediumScreen && !this.disableTooltip,
                backgroundColor: 'white',
                titleColor: 'black',
                bodyColor: 'black',
                padding: 15,
                cornerRadius: 2,
                titleFont: { size: 14 },
                bodyFont: { size: 13 }
              }
            },
            ...this.chartOptions
          }
        });
      } catch (e) {
        console.error('Error creating chart:', e);
      }
    },
    applyData() {
      if (!this.chart || this.isDestroyed) return;
      try {
        const modified = this.baseModifiedData();
        // Replace datasets & labels without full destroy
        this.chart.data.labels = modified.labels;
        this.chart.data.datasets = modified.datasets;
      } catch (e) {
        console.error('Error applying data:', e);
        this.rebuildChart();
      }
    },
    scheduleUpdate() {
      if (this.pauseUpdates || this.isDestroyed || !this.chart) return;
      if (this.rafId) return;
      this.rafId = requestAnimationFrame(() => {
        this.rafId = null;
        this.safeUpdate();
      });
    },
    safeUpdate() {
      if (!this.chart || this.isDestroyed) return;
      try {
        this.chart.update('none'); // no animation
      } catch (e) {
        console.error('Chart update failed, rebuilding:', e);
        this.rebuildChart();
      }
    },
    destroyChart() {
      if (this.isDestroyed) return;
      this.isDestroyed = true;
      if (this.rafId) {
        cancelAnimationFrame(this.rafId);
        this.rafId = null;
      }
      if (this.debouncedDataUpdate?.cancel) {
        this.debouncedDataUpdate.cancel();
      }
      const canvas = this.$refs.canvas;
      if (canvas) {
        const existing = Chart.getChart(canvas);
        try { 
            existing?.destroy();
        } catch (e) {
            console.error('Error destroying chart:', e);
        }
        const ctx = canvas.getContext('2d');
        if (ctx) ctx.clearRect(0, 0, canvas.width, canvas.height);
      }
      this.chart = null;
    },
    setupVisibilityHandling() {
      this.visibilityHandler = () => {
        this.pauseUpdates = document.hidden;
      };
      document.addEventListener('visibilitychange', this.visibilityHandler, { passive: true });
    },
    setupIntersectionObserver() {
      const el = this.$el;
      if (!('IntersectionObserver' in window) || !el) return;
      this.io = new IntersectionObserver((entries) => {
        this.pauseUpdates = !entries[0].isIntersecting;
      }, { threshold: 0.1 });
      this.io.observe(el);
    }
  }
});
</script>

<style scoped>
canvas {
  width: 100% !important;
  height: 100% !important;
}
</style>